<template>
  <div class="container-fluid px-3 py-3">
    <!-- Cabeçalho: empresa/organização ativas + grupos -->
    <div class="row mb-3">
      <div class="col-12">
        <div class="card shadow-sm">
          <div class="card-body bg-body-tertiary p-3 rounded d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div>
              <h5 class="mb-1 text-body-emphasis">
                <i class="bi bi-gear-wide-connected me-2"></i>Central de Seeds
              </h5>

              <div v-if="empresaAtiva" class="badge bg-success bg-opacity-10 text-success border border-success-subtle me-1">
                <i class="bi bi-building me-1"></i>Empresa ativa: {{ nomeEmpresaAtiva }}
              </div>
              <div v-else class="badge bg-warning bg-opacity-10 text-warning border border-warning-subtle me-1">
                <i class="bi bi-exclamation-triangle-fill me-1"></i>Nenhuma empresa selecionada — selecione no topo
              </div>
              <div v-if="organizacaoAtiva" class="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle">
                <i class="bi bi-diagram-3 me-1"></i>Organização ativa: #{{ organizacaoAtiva }}
              </div>
              <div v-else class="badge bg-warning bg-opacity-10 text-warning border border-warning-subtle">
                <i class="bi bi-exclamation-triangle-fill me-1"></i>Nenhuma organização selecionada
              </div>
            </div>

            <div class="btn-group" role="group" aria-label="Grupo da central de seeds">
              <button
                v-for="g in grupos"
                :key="g.valor"
                type="button"
                class="btn"
                :class="grupoAtivo === g.valor ? 'btn-primary' : 'btn-outline-primary'"
                @click="definirGrupo(g.valor)"
              >
                <i :class="[g.icone, 'me-1']"></i>{{ g.label }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Grid principal: checklist (esquerda) + relatórios (direita) -->
    <div class="row g-3">
      <div class="col-12 col-xl-7">
        <SeedChecklist
          :seeds="seedsDoGrupoAtual"
          :status-seeds="statusSeeds"
          :progresso="progresso"
          :empresa-ativa="empresaAtiva"
          :organizacao-ativa="organizacaoAtiva"
          :carregando="carregandoStatus"
          :configuracoes-seeds="configuracoesSeeds"
          @acao="aoExecutarAcao"
        />
      </div>

      <div class="col-12 col-xl-5">
        <div class="d-flex flex-column gap-3">
          <SeedTimelineRelatorios
            v-model:somente-erros="filtroSomenteErros"
            v-model:data-inicio="filtroDataInicio"
            v-model:data-fim="filtroDataFim"
            :lotes="lotesVisiveis"
            :selecionado-id="loteSelecionadoId"
            @selecionar="selecionarLote"
            @atualizar="carregarExecucoesPersistidas"
            @aplicar-hoje="aplicarFiltroHoje"
          />
          <SeedRelatorioDetalhe :lote="loteSelecionado" />
        </div>
      </div>
    </div>

    <SeedConfigModal
      v-model="modalConfigAberto"
      :seed="seedEmConfig"
      :config="seedEmConfig ? obterConfig(seedEmConfig) : {}"
      @salvar="aoSalvarConfig"
      @executar="aoExecutarConfig"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useUiStore } from '@/stores/ui'
import SeedChecklist from './SeedChecklist.vue'
import SeedTimelineRelatorios from './SeedTimelineRelatorios.vue'
import SeedRelatorioDetalhe from './SeedRelatorioDetalhe.vue'
import SeedConfigModal from './SeedConfigModal.vue'
import { useSeeds } from '@/composables/useSeeds'

const emit = defineEmits(['show-notification'])

const {
  empresaAtiva,
  organizacaoAtiva,
  grupoAtivo,
  definirGrupo,
  seedsDoGrupoAtual,
  statusSeeds,
  progresso,
  carregandoStatus,
  lotesVisiveis,
  loteSelecionado,
  loteSelecionadoId,
  selecionarLote,
  carregarExecucoesPersistidas,
  filtroSomenteErros,
  filtroDataInicio,
  filtroDataFim,
  aplicarFiltroHoje,
  configuracoesSeeds,
  obterConfig,
  salvarConfig,
  seedTemConfig,
  executarSeed,
  GRUPO_MASTER,
  GRUPO_ORGANIZACAO
} = useSeeds()

const grupos = [
  { valor: GRUPO_MASTER, label: 'Master', icone: 'bi-hdd-stack' },
  { valor: GRUPO_ORGANIZACAO, label: 'Organização', icone: 'bi-diagram-3' }
]

const uiStore = useUiStore()

// Modal de configurações do seed (engrenagem)
const modalConfigAberto = ref(false)
const seedEmConfig = ref(null)

// Reutiliza a lista de empresas já carregada pelo header (MainDashboard),
// evitando uma chamada extra a /Empresas.
const nomeEmpresaAtiva = computed(() => {
  const id = empresaAtiva.value
  if (!id) return null
  const e = uiStore.listaEmpresasParaSelecao.find(x => x.idEmpresa === id)
  return e?.nomeEmpresa || `Empresa #${id}`
})

function abrirConfig (seed) {
  seedEmConfig.value = seed
  modalConfigAberto.value = true
}

function aoSalvarConfig ({ seed, valores }) {
  salvarConfig(seed, valores)
  emit('show-notification', { message: `Configurações de "${seed.nome}" salvas.`, type: 'success' })
}

async function executarAcao (seed, tipo) {
  const resultado = await executarSeed(seed, tipo)
  if (resultado?.relatorio) {
    const r = resultado.relatorio
    const type = r.status === 'erro' ? 'error' : (r.status === 'aviso' ? 'warning' : 'success')
    emit('show-notification', { message: `${r.nomeSeed}: ${r.titulo}`, type })
  }
}

// Botão "Executar" do modal: salva as configurações e executa o seed
async function aoExecutarConfig ({ seed, valores }) {
  salvarConfig(seed, valores)
  await executarAcao(seed, 'executar')
}

async function aoExecutarAcao ({ seed, tipo }) {
  if (tipo === 'configurar') {
    abrirConfig(seed)
    return
  }

  // Seeds que exigem configuração: abre o modal antes de executar,
  // garantindo que o usuário revise/defina os parâmetros.
  if (tipo === 'executar' && seedTemConfig(seed)) {
    abrirConfig(seed)
    return
  }

  await executarAcao(seed, tipo)
}

onMounted(() => {
  // Idempotente: só busca se o header ainda não populou a lista
  uiStore.buscarEmpresasParaSelecao()
})
</script>
